import { formatBytesWithMagnitude } from "@ui/format";
import { secondsToTime } from "@utils/time";
import type { Accessor } from "solid-js";
import { createEffect, createSignal } from "solid-js";
import "./Slider.css";

const clamp = (value: number, min: number, max: number) =>
	Math.max(min, Math.min(max, value));

/** Decimal places implied by `step` (e.g. 0.1 → 1), for fixing float drift after `min + n * step`. */
function decimalPlacesForStep(step: number): number {
	if (!Number.isFinite(step) || step === 0) {
		return 0;
	}
	const str = step.toString().toLowerCase();
	if (str.includes("e-")) {
		const m = /e-(\d+)/.exec(str);
		return m ? Number.parseInt(m[1], 10) : 0;
	}
	const dot = str.indexOf(".");
	return dot === -1 ? 0 : str.length - dot - 1;
}

/**
 * Nearest step index from `min` (0 = min, 1 = min + step, …).
 * Float error on `(value - min) / step` can sit just below a half-integer and
 * make `Math.round` pick the wrong step; a tiny nudge fixes that.
 */
function stepsFromMin(value: number, min: number, step: number): number {
	const q = (value - min) / step;
	const tol = Math.max(Number.EPSILON * 10, 1e-9 * Math.max(1, Math.abs(q)));
	const nudge = q === 0 ? tol : Math.sign(q) * tol;
	return Math.round(q + nudge);
}

const roundToStep = (value: number, step: number, min: number) => {
	if (step <= 0) return value;
	const steps = stepsFromMin(value, min, step);
	const raw = min + steps * step;
	const dp = decimalPlacesForStep(step);
	return dp === 0 ? raw : Number(raw.toFixed(dp));
};

const formatCompact = (value: number): string => {
	if (value >= 1_000_000_000)
		return `${(value / 1_000_000_000).toFixed(value % 1_000_000_000 === 0 ? 0 : 1)}b`;
	if (value >= 1_000_000)
		return `${(value / 1_000_000).toFixed(value % 1_000_000 === 0 ? 0 : 1)}m`;
	if (value >= 1_000)
		return `${(value / 1_000).toFixed(value % 1_000 === 0 ? 0 : 1)}k`;
	return String(value);
};

/**
 * Exponential scale helpers.
 * The native input operates on a linear 0..RESOLUTION range.
 * We map that to real values via a power curve so small values
 * get more precision and large values compress toward the end.
 */
const EXP_RESOLUTION = 10000;
const DEFAULT_EXPONENT = 3;

const expPosToValue = (
	pos: number,
	min: number,
	max: number,
	exponent: number,
): number => {
	const t = pos / EXP_RESOLUTION; // 0..1
	return min + (max - min) * t ** exponent;
};

const valueToExpPos = (
	value: number,
	min: number,
	max: number,
	exponent: number,
): number => {
	const t = (value - min) / (max - min); // 0..1
	return Math.round(EXP_RESOLUTION * Math.max(0, t) ** (1 / exponent));
};

type SharedSliderProps = {
	/** Minimum value of the slider (inclusive). */
	min: number;
	/** Maximum value of the slider (inclusive). */
	max: number;
	/**
	 * Increment between allowed values. Values are snapped to `min + n * step`.
	 * @default 1
	 */
	step?: number;
	/**
	 * Visual style for the track and thumb.
	 * `white` uses a light track and thumb for dark or coloured backgrounds (e.g. video overlays).
	 * @default "default"
	 */
	theme?: "default" | "white";
	/**
	 * Layout of the track. `vertical` runs bottom (`min`) → top (`max`).
	 * Single mode only; ignored when `mode="range"`.
	 * @default "horizontal"
	 */
	orientation?: "horizontal" | "vertical";
	/** Visible label shown above the track; also used for `id` generation and default `aria-label` text. */
	label?: string;
	/** When true, shows the current value (or low–high range) beside the label. */
	showValue?: boolean;
	/**
	 * Where to render the `showValue` readout relative to the track.
	 * @default "top"
	 */
	valuePlacement?: "top" | "bottom";
	/** When true, shows formatted `min` and `max` at the ends of the track. */
	showRange?: boolean;
	/** When true, formats displayed numbers with compact suffixes (`1k`, `1m`, `1b`). Ignored when `valueFormat` is `"time"` or `"bytes"`. */
	formatLargeNumbers?: boolean;
	/**
	 * How values are shown in labels, range ends, and `aria-valuetext`.
	 * `time` treats values as seconds and formats as `M:SS` (or `H:MM:SS` when needed).
	 * `bytes` treats values as byte counts (KB, MB, GB, …).
	 * @default "numeric"
	 */
	valueFormat?: "numeric" | "time" | "bytes";
	/**
	 * Maps the native control through a power curve so low values get finer control.
	 * Pass `true` for the default exponent (3), or a number for a custom exponent.
	 */
	exponential?: boolean | number;
	/** When true, shows editable numeric input(s) below the track for precise entry. */
	showInputs?: boolean;
	/** When true, the slider and inputs cannot be interacted with. */
	disabled?: boolean;
	/**
	 * Base id for the control and derived element ids (`-low`, `-high`, `-label`).
	 * Defaults from `label` or `"slider-control"`.
	 */
	id?: string;
};

type SingleSliderProps = SharedSliderProps & {
	/**
	 * Single-thumb slider. Omitted or `"single"` (default).
	 * @default "single"
	 */
	mode?: "single";
	/** Current value; must stay between `min` and `max`. */
	value: Accessor<number>;
	/** Called when the user changes the value via the track, keyboard, or numeric input. */
	onChange: (value: number) => void;
	/** Accessible name for the range input when `label` is not sufficient. */
	"aria-label"?: string;
};

type RangeSliderProps = SharedSliderProps & {
	/** Dual-thumb range slider; `orientation` is not supported in this mode. */
	mode: "range";
	/** Current `[low, high]` pair; each bound must stay between `min` and `max`, with `low <= high`. */
	value: Accessor<[number, number]>;
	/** Called when either thumb changes; receives the updated `[low, high]` tuple. */
	onChange: (value: [number, number]) => void;
	/** Accessible names for the low and high range inputs, in that order. */
	"aria-label"?: [string, string];
};

export type SliderProps = SingleSliderProps | RangeSliderProps;

const DEFAULT_STEP = 1;

/**
 * Single value or range slider with step support and optional exponential scale.
 * Set `orientation="vertical"` for a bottom (min) → top (max) track (single mode only; ignored for range).
 *
 * [Design System →](http://localhost:5174/component/Slider)
 */
export const Slider = (props: SliderProps) => {
	const isWhiteTheme = () => props.theme === "white";
	const step = () => props.step ?? DEFAULT_STEP;
	const isRange = () => props.mode === "range";
	const isVertical = () =>
		!isRange() && (props.orientation ?? "horizontal") === "vertical";
	const isExp = () => !!props.exponential;
	const exponent = () =>
		typeof props.exponential === "number"
			? props.exponential
			: DEFAULT_EXPONENT;

	// Exponential: native input uses 0..EXP_RESOLUTION (linear).
	// Linear: native input uses real min..max.
	const sliderMin = () => (isExp() ? 0 : props.min);
	const sliderMax = () => (isExp() ? EXP_RESOLUTION : props.max);
	const sliderStep = () => (isExp() ? 1 : step());

	// Convert native slider position → real value
	const posToValue = (pos: number): number => {
		if (isExp()) {
			const raw = expPosToValue(pos, props.min, props.max, exponent());
			return roundToStep(raw, step(), props.min);
		}
		return roundToStep(pos, step(), props.min);
	};

	// Convert real value → native slider position
	const valueToPos = (value: number): number => {
		if (isExp()) {
			return valueToExpPos(value, props.min, props.max, exponent());
		}
		return value;
	};

	// Percentage for fill bar (always based on position in slider space)
	const valueToPct = (value: number): number => {
		const pos = valueToPos(value);
		return ((pos - sliderMin()) / (sliderMax() - sliderMin())) * 100;
	};

	// Single value state
	const [singleValue, setSingleValue] = createSignal(
		isRange() ? 0 : (props as SingleSliderProps).value(),
	);

	// Range value state
	const [rangeLow, setRangeLow] = createSignal(
		isRange() ? (props as RangeSliderProps).value()[0] : props.min,
	);
	const [rangeHigh, setRangeHigh] = createSignal(
		isRange() ? (props as RangeSliderProps).value()[1] : props.max,
	);

	createEffect(() => {
		if (isRange()) {
			const [low, high] = (props as RangeSliderProps).value();
			setRangeLow(low);
			setRangeHigh(high);
		} else {
			setSingleValue((props as SingleSliderProps).value());
		}
	});

	// Refs for imperatively snapping thumbs back when they would cross
	let lowRef: HTMLInputElement | undefined;
	let highRef: HTMLInputElement | undefined;

	const baseId = () =>
		props.id ??
		`slider-${props.label?.toLowerCase().replace(/\s+/g, "-") ?? "control"}`;
	const singleId = () => baseId();
	const lowId = () => `${baseId()}-low`;
	const highId = () => `${baseId()}-high`;

	// Single mode handlers
	const handleSingleChange = (e: Event & { target: HTMLInputElement }) => {
		const raw = Number((e.target as HTMLInputElement).value);
		const v = posToValue(raw);
		const clamped = clamp(v, props.min, props.max);
		setSingleValue(clamped);
		(props as SingleSliderProps).onChange(clamped);
	};

	// Step a value up or down. In exponential mode, step by ~1% of the
	// internal slider range so each arrow press is visually meaningful.
	// In linear mode, step by props.step.
	const EXP_KEY_STEP = Math.max(1, Math.round(EXP_RESOLUTION / 100));

	const stepValue = (current: number, direction: 1 | -1): number => {
		const st = step();
		const minV = props.min;
		if (isExp()) {
			const snapped = roundToStep(current, st, minV);
			const currentPos = valueToPos(snapped);
			const nextPos = clamp(currentPos + direction * EXP_KEY_STEP, sliderMin(), sliderMax());
			let next = posToValue(nextPos);
			// If roundToStep collapsed back to the same value (e.g. near 0
			// where exponential values are tiny relative to step), force
			// movement by at least one step.
			if (next === snapped && nextPos !== currentPos) {
				const n = stepsFromMin(snapped, minV, st) + direction;
				next = roundToStep(minV + n * st, st, minV);
			}
			return next;
		}
		const n = stepsFromMin(current, minV, st) + direction;
		return roundToStep(minV + n * st, st, minV);
	};

	// Keyboard handler for single mode
	const handleSingleKeyDown = (e: KeyboardEvent) => {
		if (props.disabled) return;
		let dir: 1 | -1;
		if (e.key === "ArrowRight" || e.key === "ArrowUp") dir = 1;
		else if (e.key === "ArrowLeft" || e.key === "ArrowDown") dir = -1;
		else return;
		e.preventDefault();
		const v = clamp(stepValue(singleValue(), dir), props.min, props.max);
		setSingleValue(v);
		(props as SingleSliderProps).onChange(v);
	};

	// Keyboard handlers for range mode
	const handleRangeLowKeyDown = (e: KeyboardEvent) => {
		if (props.disabled) return;
		let dir: 1 | -1;
		if (e.key === "ArrowRight" || e.key === "ArrowUp") dir = 1;
		else if (e.key === "ArrowLeft" || e.key === "ArrowDown") dir = -1;
		else return;
		e.preventDefault();
		const v = clamp(stepValue(rangeLow(), dir), props.min, rangeHigh());
		if (lowRef) lowRef.value = String(valueToPos(v));
		setRangeLow(v);
		(props as RangeSliderProps).onChange([v, rangeHigh()]);
	};

	const handleRangeHighKeyDown = (e: KeyboardEvent) => {
		if (props.disabled) return;
		let dir: 1 | -1;
		if (e.key === "ArrowRight" || e.key === "ArrowUp") dir = 1;
		else if (e.key === "ArrowLeft" || e.key === "ArrowDown") dir = -1;
		else return;
		e.preventDefault();
		const v = clamp(stepValue(rangeHigh(), dir), rangeLow(), props.max);
		if (highRef) highRef.value = String(valueToPos(v));
		setRangeHigh(v);
		(props as RangeSliderProps).onChange([rangeLow(), v]);
	};

	// Range mode handlers: keep low <= high and within [min, max].
	// Imperatively set input.value to snap the thumb back when it would cross.
	const handleRangeLowChange = (e: Event & { target: HTMLInputElement }) => {
		const raw = Number(e.target.value);
		const v = posToValue(raw);
		const clamped = clamp(v, props.min, rangeHigh());
		if (lowRef) lowRef.value = String(valueToPos(clamped));
		setRangeLow(clamped);
		(props as RangeSliderProps).onChange([clamped, rangeHigh()]);
	};

	const handleRangeHighChange = (e: Event & { target: HTMLInputElement }) => {
		// The high input has pointer-events: none (so track clicks fall through
		// to the low input) but its thumb pseudo-element has pointer-events: auto.
		// Dragging the thumb fires onInput but doesn't grant focus, so we do it
		// explicitly here so keyboard events work after a drag.
		if (highRef && document.activeElement !== highRef) highRef.focus();
		const raw = Number(e.target.value);
		const v = posToValue(raw);
		const clamped = clamp(v, rangeLow(), props.max);
		if (highRef) highRef.value = String(valueToPos(clamped));
		setRangeHigh(clamped);
		(props as RangeSliderProps).onChange([rangeLow(), clamped]);
	};

	const singleFillStyle = () => {
		const pct = valueToPct(singleValue());
		if (isVertical()) {
			const full =
				singleValue() >= props.max ||
				pct >= 99.5;
			return {
				height: `${pct}%`,
				...(full ? { "border-radius": "9999px" } : {}),
			};
		}
		return { width: `${pct}%` };
	};

	const rangeFillStyle = () => {
		const left = valueToPct(rangeLow());
		const right = valueToPct(rangeHigh());
		return { left: `${left}%`, width: `${right - left}%` };
	};

	const fmt = (v: number) => {
		const valueFormat = props.valueFormat ?? "numeric";
		if (valueFormat === "time") {
			return secondsToTime(v);
		}
		if (valueFormat === "bytes") {
			return formatBytesWithMagnitude(v, 1);
		}
		return props.formatLargeNumbers ? formatCompact(v) : String(v);
	};
	const singleValueText = () => fmt(singleValue());
	const rangeValueText = () => `${fmt(rangeLow())} – ${fmt(rangeHigh())}`;

	// Handlers for the editable numeric inputs (showInputs)
	const commitSingleInput = (e: Event & { target: HTMLInputElement }) => {
		const raw = Number(e.target.value);
		if (Number.isNaN(raw) || e.target.value === "") {
			e.target.value = String(singleValue());
			return;
		}
		const v = clamp(roundToStep(raw, step(), props.min), props.min, props.max);
		e.target.value = String(v);
		setSingleValue(v);
		(props as SingleSliderProps).onChange(v);
	};

	const commitRangeLowInput = (e: Event & { target: HTMLInputElement }) => {
		const raw = Number(e.target.value);
		if (Number.isNaN(raw) || e.target.value === "") {
			e.target.value = String(rangeLow());
			return;
		}
		const v = clamp(roundToStep(raw, step(), props.min), props.min, rangeHigh());
		e.target.value = String(v);
		if (lowRef) lowRef.value = String(valueToPos(v));
		setRangeLow(v);
		(props as RangeSliderProps).onChange([v, rangeHigh()]);
	};

	const commitRangeHighInput = (e: Event & { target: HTMLInputElement }) => {
		const raw = Number(e.target.value);
		if (Number.isNaN(raw) || e.target.value === "") {
			e.target.value = String(rangeHigh());
			return;
		}
		const v = clamp(roundToStep(raw, step(), props.min), rangeLow(), props.max);
		e.target.value = String(v);
		if (highRef) highRef.value = String(valueToPos(v));
		setRangeHigh(v);
		(props as RangeSliderProps).onChange([rangeLow(), v]);
	};

	const handleInputKeyDown = (e: KeyboardEvent) => {
		if (e.key === "Enter") (e.target as HTMLInputElement).blur();
	};

	const valuePlacement = () => props.valuePlacement ?? "top";
	const showValueOnTop = () => props.showValue && valuePlacement() === "top";
	const showValueOnBottom = () =>
		props.showValue && valuePlacement() === "bottom";

	const valueReadout = () => (
		<span class="gvtui-slider-value" aria-live="polite">
			{isRange() ? rangeValueText() : singleValueText()}
		</span>
	);

	return (
		<div
			class="gvtui-slider"
			classList={{
				"gvtui-slider--range": isRange(),
				"gvtui-slider--disabled": props.disabled,
				"gvtui-slider--theme-white": isWhiteTheme(),
				"gvtui-slider--vertical": isVertical(),
			}}
			role={isRange() ? "group" : undefined}
		>
			{(props.label || showValueOnTop()) && (
				<div class="gvtui-slider-header">
					{props.label && (
						<label
							class="gvtui-slider-label"
							for={isRange() ? undefined : singleId()}
							id={`${baseId()}-label`}
						>
							{props.label}
						</label>
					)}
					{showValueOnTop() && valueReadout()}
				</div>
			)}
			<div class="gvtui-slider-row">
				{props.showRange && (
					<span class="gvtui-slider-range-min" aria-hidden="true">
						{fmt(props.min)}
					</span>
				)}
				<div class="gvtui-slider-track-container">
					<div class="gvtui-slider-track-background" aria-hidden="true" />
					<div
						class="gvtui-slider-track-fill"
						style={isRange() ? rangeFillStyle() : singleFillStyle()}
						aria-hidden="true"
					/>
					{isRange() ? (
						<>
							<input
								ref={lowRef}
								type="range"
								class="gvtui-slider-input gvtui-slider-input--low"
								id={lowId()}
								min={sliderMin()}
								max={sliderMax()}
								step={sliderStep()}
								value={valueToPos(rangeLow())}
								onInput={handleRangeLowChange}
								onKeyDown={handleRangeLowKeyDown}
								disabled={props.disabled}
								aria-valuemin={props.min}
								aria-valuemax={rangeHigh()}
								aria-valuenow={rangeLow()}
								aria-label={
									props["aria-label"]?.[0] ??
									`${props.label ?? "Minimum"} (low)`
								}
								aria-describedby={`${baseId()}-label`}
							/>
							<input
								ref={highRef}
								type="range"
								class="gvtui-slider-input gvtui-slider-input--high"
								id={highId()}
								min={sliderMin()}
								max={sliderMax()}
								step={sliderStep()}
								value={valueToPos(rangeHigh())}
								onInput={handleRangeHighChange}
								onKeyDown={handleRangeHighKeyDown}
								disabled={props.disabled}
								aria-valuemin={rangeLow()}
								aria-valuemax={props.max}
								aria-valuenow={rangeHigh()}
								aria-label={
									props["aria-label"]?.[1] ??
									`${props.label ?? "Maximum"} (high)`
								}
								aria-describedby={`${baseId()}-label`}
							/>
						</>
					) : (
						<input
							type="range"
							class="gvtui-slider-input"
							id={singleId()}
							min={sliderMin()}
							max={sliderMax()}
							step={sliderStep()}
							value={valueToPos(singleValue())}
							onInput={handleSingleChange}
							onKeyDown={handleSingleKeyDown}
							disabled={props.disabled}
							aria-valuemin={props.min}
							aria-valuemax={props.max}
							aria-valuenow={singleValue()}
							aria-valuetext={singleValueText()}
							aria-orientation={isVertical() ? "vertical" : "horizontal"}
							aria-label={
								(props as SingleSliderProps)["aria-label"] ??
								props.label ??
								"Slider"
							}
							aria-describedby={`${baseId()}-label`}
						/>
					)}
				</div>
				{props.showRange && (
					<span class="gvtui-slider-range-max" aria-hidden="true">
						{fmt(props.max)}
					</span>
				)}
			</div>
			{showValueOnBottom() && (
				<div
					class="gvtui-slider-row gvtui-slider-value-row"
					classList={{ "gvtui-slider-value-row--range": props.showRange }}
				>
					{props.showRange && (
						<span
							class="gvtui-slider-range-min gvtui-slider-value-row__gutter"
							aria-hidden="true"
						>
							{fmt(props.min)}
						</span>
					)}
					<div class="gvtui-slider-value-row__value">{valueReadout()}</div>
					{props.showRange && (
						<span
							class="gvtui-slider-range-max gvtui-slider-value-row__gutter"
							aria-hidden="true"
						>
							{fmt(props.max)}
						</span>
					)}
				</div>
			)}
			{props.showInputs && (
				<div class="gvtui-slider-inputs">
					{isRange() ? (
						<>
							<input
								type="text"
								inputMode="numeric"
								class="gvtui-slider-numeric-input"
								value={rangeLow()}
								onBlur={commitRangeLowInput}
								onKeyDown={handleInputKeyDown}
								disabled={props.disabled}
								aria-label={`${props.label ?? "Minimum"} value`}
							/>
							<span class="gvtui-slider-input-separator">–</span>
							<input
								type="text"
								inputMode="numeric"
								class="gvtui-slider-numeric-input"
								value={rangeHigh()}
								onBlur={commitRangeHighInput}
								onKeyDown={handleInputKeyDown}
								disabled={props.disabled}
								aria-label={`${props.label ?? "Maximum"} value`}
							/>
						</>
					) : (
						<input
							type="text"
							inputMode="numeric"
							class="gvtui-slider-numeric-input"
							value={singleValue()}
							onBlur={commitSingleInput}
							onKeyDown={handleInputKeyDown}
							disabled={props.disabled}
							aria-label={`${props.label ?? "Slider"} value`}
						/>
					)}
				</div>
			)}
		</div>
	);
};
